import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowRight, ArrowLeft, Check, Loader2 } from "lucide-react";
import { SouthEastFundTile } from "./leadForm/SouthEastFundTile";
import { Step1Amount } from "./leadForm/Step1Amount";
import { Step2LoanDetails } from "./leadForm/Step2LoanDetails";
import { Step3ContactDetails } from "./leadForm/Step3ContactDetails";
import { useCompanySearch } from "./leadForm/useCompanySearch";
import { isStepValid } from "./leadForm/validation";
import { INITIAL_FORM_STATE, stepVariants } from "./leadForm/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_STEPS = 3;

const SuccessPanel = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    className="bg-white rounded-2xl p-10 border border-[#E5E5E0] text-center"
    data-testid="lead-success"
  >
    <div className="w-16 h-16 rounded-full bg-[#052F24] flex items-center justify-center mx-auto mb-6">
      <Check className="text-[#DFFF00]" size={30} />
    </div>
    <h3 className="font-display text-3xl font-extrabold tracking-tight mb-3">You're in good hands.</h3>
    <p className="text-[#052F24]/60 font-light">
      A dedicated Growth Funds specialist will call you shortly to walk through your options. No credit impact.
    </p>
  </motion.div>
);

export const LeadForm = () => {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(INITIAL_FORM_STATE);
  const companySearch = useCompanySearch();

  const set = (key, value, extra = {}) => setForm((f) => ({ ...f, [key]: value, ...extra }));

  const canNext = () => isStepValid(step, form);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
    } catch (e) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((step + 1) / TOTAL_STEPS) * 100;

  if (done) return <SuccessPanel />;

  return (
    <div className="space-y-4">
      <SouthEastFundTile />

      <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E5E5E0] shadow-[0_20px_60px_-30px_rgba(5,47,36,0.4)]" data-testid="lead-form">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[11px] font-bold tracking-[0.2em] text-[#052F24]/50 uppercase">
            Step {step + 1} of {TOTAL_STEPS}
          </span>
          <span className="text-[11px] font-bold tracking-[0.2em] text-[#052F24]/50 uppercase">No credit impact</span>
        </div>
        <div className="h-1.5 w-full bg-[#052F24]/10 rounded-full overflow-hidden mb-7">
          <motion.div
            className="h-full bg-[#052F24]"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            {step === 0 && <Step1Amount value={form.loan_amount} onSelect={(v) => set("loan_amount", v)} />}
            {step === 1 && <Step2LoanDetails form={form} onSet={set} />}
            {step === 2 && <Step3ContactDetails form={form} onSet={set} error={error} companySearch={companySearch} />}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center gap-3 mt-8">
          {step > 0 && (
            <button
              onClick={() => setStep((s) => s - 1)}
              className="flex items-center gap-1.5 px-4 py-3.5 rounded-full text-sm font-semibold text-[#052F24]/70 hover:text-[#052F24] transition-colors"
              data-testid="form-back-btn"
            >
              <ArrowLeft size={16} /> Back
            </button>
          )}
          {step < TOTAL_STEPS - 1 ? (
            <button
              onClick={() => canNext() && setStep((s) => s + 1)}
              disabled={!canNext()}
              className="group flex-1 flex items-center justify-center gap-2 bg-[#052F24] text-[#DFFF00] font-bold py-4 rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#0a3d30] transition-colors"
              data-testid="form-next-btn"
            >
              Continue
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={!canNext() || submitting}
              className="flex-1 flex items-center justify-center gap-2 bg-[#052F24] text-[#DFFF00] font-bold py-4 rounded-full disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#0a3d30] transition-colors"
              data-testid="form-submit-btn"
            >
              {submitting ? <Loader2 className="animate-spin" size={18} /> : <>See my options <ArrowRight size={18} /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
